import { Injectable } from '@angular/core';

const STORAGE_KEY = 'situScratch.themeUsage.v1';
const MAX_TRACKED_PER_SCOPE = 60;

interface IThemeUsage {
  count: number;
  lastUsedIso: string;
}

/** Usage counts keyed by scope (user + company), then by style id. */
interface IUsageStore {
  [scope: string]: { [styleId: string]: IThemeUsage };
}

/**
 * How often each theme has been used in this user's scratch renders, so the ones they reach for
 * most are one click away. Persisted in localStorage — a cookie would cap at ~4 KB and ride along
 * on every HTTP request, which is the wrong tool for this (same reasoning as RecentHomesService).
 *
 * Scoped per user + company, since styles/themes are company-scoped and a machine may be shared.
 */
@Injectable({ providedIn: 'root' })
export class ScratchThemeUsageService {

  static scopeKey(userId: string | undefined, companyId: string | undefined): string {
    return `${userId ?? 'anon'}:${companyId ?? 'none'}`;
  }

  /** Style ids used most in this scope, most popular first. */
  top(scope: string, limit: number): string[] {
    const usage = this.read()[scope] ?? {};
    return Object.keys(usage)
      .sort((a, b) => this.compare(usage[a], usage[b]))
      .slice(0, limit);
  }

  /** Bump the use count for each style id that went into a scratch render. */
  record(scope: string, styleIds: string[]): void {
    if (!styleIds || styleIds.length === 0) {
      return;
    }

    const store = this.read();
    const usage = store[scope] ?? {};
    const nowIso = new Date().toISOString();

    styleIds.forEach(id => {
      usage[id] = { count: (usage[id]?.count ?? 0) + 1, lastUsedIso: nowIso };
    });

    store[scope] = this.trim(usage);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch {
      // Storage full or unavailable — counts just don't persist this time.
    }
  }

  /** Most popular first, ties broken by most recently used. */
  private compare(a: IThemeUsage, b: IThemeUsage): number {
    return (b.count - a.count) || (b.lastUsedIso ?? '').localeCompare(a.lastUsedIso ?? '');
  }

  /** Keep the store bounded — drop the long tail of one-off themes. */
  private trim(usage: { [styleId: string]: IThemeUsage }): { [styleId: string]: IThemeUsage } {
    const ids = Object.keys(usage);
    if (ids.length <= MAX_TRACKED_PER_SCOPE) {
      return usage;
    }

    const trimmed: { [styleId: string]: IThemeUsage } = {};
    ids.sort((a, b) => this.compare(usage[a], usage[b]))
      .slice(0, MAX_TRACKED_PER_SCOPE)
      .forEach(id => trimmed[id] = usage[id]);
    return trimmed;
  }

  private read(): IUsageStore {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return {};
      }
      const parsed = JSON.parse(raw);
      return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch {
      return {};
    }
  }
}
